import React, { useEffect, useMemo, useState } from 'react';
import TradeJobChecklistExecutionView from './TradeJobChecklistExecutionView';
import TradeJobCreateInvoiceView from './TradeJobCreateInvoiceView';
import TradeJobCreateQuoteView from './TradeJobCreateQuoteView';
import LinkInvoiceQuoteFeature from './LinkInvoiceQuoteFeature';
import ProfitForecastView from './ProfitForecastView';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SaveOutlined, UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import AddTradeJob from '../service/AddTradeJob';
import FetchBusinessCustomers from '../service/FetchBusinessCustomers';
import FetchTradeJobs from '../service/FetchTradeJobs';
import SaveBusinessCustomer from '../service/SaveBusinessCustomer';
import FetchEmployeesService from '../../roster/FetchEmployeesService';
import FetchLocationsService from '../../roster/FetchLocationsService';
import { baseUrl_SPB } from '../../BaseUrl';
import axios from 'axios';
import AddBookedItem from '../../roster/AddBookedItemUA';
import DeleteBookedItem from '../../roster/DeleteBookedItemUA';

const { Text, Title } = Typography;
const { TextArea } = Input;

const STEP_TITLES = [
  'Customer & Job Details',
  'Scheduling',
  'Checklist & Execution',
  'Admin / Metadata',
];

const JOB_STATUSES = ['Initial', 'Inspection', 'Quote', 'In-progress', 'Invoice', 'Completed', 'Incomplete', 'Cancelled'];
const JOB_CATEGORIES = ['After-Hours', 'Standard', 'VIP', 'Warranty'];
const CHECKLIST_BASE = [
  { text: 'Inspection', completed: false, notes: '' },
  { text: 'Quote', completed: false, notes: '' },
  { text: 'Complete', completed: false, notes: '' },
  { text: 'Invoice', completed: false, notes: '' },
];
const SLOT_OPTIONS = Array.from({ length: 24 }, (_, index) => ({
  label: `${String(index).padStart(2, '0')}:00`,
  value: `${String(index).padStart(2, '0')}:00`,
}));

export default function TradeJobFormView({
  open,
  onClose,
  onSaved,
  token,
  additionalUserInfo,
  initialJob,
  presetLocationId,
  asPage = false,
}) {
  const [formData, setFormData] = useState({
    id: '',
    jobName: '',
    jobDetails: '',
    customerId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    jobAddress: '',
    status: 'Quote',
    locationId: '',
    selectedDate: '',
    category: 'Standard',
    poNumber: '',
    companyId: '',
    invoiceId: '',
    quoteId: '',
    bookedItemIds: []
  });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [companyLocations, setCompanyLocations] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [checkList, setCheckList] = useState(CHECKLIST_BASE);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [addCustomerSaving, setAddCustomerSaving] = useState(false);
  const [addCustomerError, setAddCustomerError] = useState('');
  const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '', businessName: '', abn: '', note: '' });
  
  const [assignees, setAssignees] = useState([]);
  const [primaryAssigneeId, setPrimaryAssigneeId] = useState('');
  const [selectedSlotsPerStaff, setSelectedSlotsPerStaff] = useState({});
  const [allJobs, setAllJobs] = useState([]);
  const [assigneePickerValue, setAssigneePickerValue] = useState(undefined);
  const [availabilityData, setAvailabilityData] = useState([]); // This will now hold the flat array for the selected day
  const [statusAuditTrail, setStatusAuditTrail] = useState([]);
  const [prevStatus, setPrevStatus] = useState('');
  const [bookedItems, setBookedItems] = useState([]);

  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedQIItem, setSelectedQIItem] = useState(null);

  const [invoice, setInvoice] = useState(null);
  const [quote, setQuote] = useState(null);

  const jobId = useMemo(() => initialJob?.id || formData.id || 'draft-job', [initialJob, formData.id]);

  const getAllStaffNames = () => {
    return (assignees || [])
      .map(a => a.assignee)
      .filter(Boolean)
      .join(', ');
  };

  function SlotGridWeb({ staffEntry, primaryAssigneeId, selectedSlots, onToggleSlot, assigneeIds }) {
    const isAssigned = assigneeIds.includes(String(staffEntry.staffId));
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{staffEntry.staffName}</span>
          {String(staffEntry.staffId) === String(primaryAssigneeId) && (
            <Tag color="blue" style={{ fontSize: 11 }}>Primary</Tag>
          )}
          {!isAssigned && (
            <Tag color="default" style={{ fontSize: 11 }}>Not assigned</Tag>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {staffEntry.slots.map((slot, i) => {
            const slotValue = SLOT_OPTIONS[i]?.value;
            const isSelected = selectedSlots.includes(slotValue) && assigneeIds.includes(String(staffEntry.staffId));
            let bg = '#f5f5f5';
            let color = '#999';
            let cursor = 'default';
            let border = '1px solid #d9d9d9';
            
            if (slot.slotForOtherJob) {
              bg = '#fafafa';
              color = '#ccc';
              border = '1px solid #e0e0e0';
            } else if (!isAssigned) {
              bg = '#fafafa';
              color = '#ccc';
            } else if (isSelected) {
              bg = '#1677ff';
              color = '#fff';
              border = '1px solid #1677ff';
              cursor = 'pointer';
            } else if (slot.available) {
              bg = '#e6f4ea';
              color = '#2e7d32';
              border = '1px solid #4caf50';
              cursor = 'pointer';
            }
            
            const disabled = slot.slotForOtherJob || !isAssigned;
            
            return (
              <span
                key={slotValue}
                onClick={() => !disabled && onToggleSlot(null, staffEntry, i, slot.available, i, slotValue, formData.selectedDate ? formData.selectedDate.format('DD-MM-YYYY') : '')}
                style={{
                  display: 'inline-block',
                  padding: '2px 7px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontFamily: 'monospace',
                  background: bg,
                  color,
                  border,
                  cursor: disabled ? 'not-allowed' : cursor,
                  userSelect: 'none',
                  opacity: disabled ? 0.5 : 1,
                }}
                title={slot.slotForOtherJob ? 'Booked by another job' : ''}
              >
                {slotValue}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  const getCustomerName = (customer) => {
    if (!customer) return '';
    const firstName = customer.firstName || '';
    const lastName = customer.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || customer.name || customer.title || customer.businessName || customer.companyName || '';
  };

  const getCustomerSearchText = (customer) => {
    return [
      getCustomerName(customer),
      customer?.email,
      customer?.phoneNumber,
      customer?.businessName,
      customer?.companyName,
      customer?.abn,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const toDayjs = (value) => {
    if (!value) return dayjs();
    if (dayjs.isDayjs(value)) return value;
    
    if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
      const [day, month, year] = value.split('-').map(Number);
      return dayjs(new Date(year, month - 1, day));
    }
    
    return dayjs(value);
  };

  const normalizeChecklist = (checkList) => {
    if (!Array.isArray(checkList) || checkList.length === 0) return CHECKLIST_BASE;
    return checkList.map((item, index) => ({
      text: item?.text || CHECKLIST_BASE[index]?.text || `Task ${index + 1}`,
      completed: item?.completed === true || item?.completed === 'true',
      notes: item?.notes || '',
    }));
  };

  const handleStatusChange = (newStatus) => {
    const fromStatus = prevStatus;
    const toStatus = newStatus;
    
    if ((fromStatus === 'Inspection' || fromStatus === 'In-progress') && toStatus !== fromStatus) {
      const staffWorkHours = (assignees || []).map(a => {
        const staffIdStr = String(a.assigneeId);
        const staffSlots = selectedSlotsPerStaff[staffIdStr] || [];
        const totalHoursWorked = staffSlots.length;
        
        return {
          staffId: staffIdStr,
          totalHours: String(totalHoursWorked),
          name: a.assignee || 'Unknown Staff',
          selectedDateString: formData.selectedDate ? formData.selectedDate.format('DD-MM-YYYY') : '',
        };
      });

      const staffSummaryString = staffWorkHours
        .map(s => `${s.name} (${s.totalHours} ${s.totalHours === '1' ? 'hr' : 'hrs'})`)
        .join(', ');

      const entry = {
        fromStatus,
        toStatus,
        changedAt: new Date().toISOString(),
        changedBy: additionalUserInfo?.id || additionalUserInfo?.email || 'unknown',
        staffNames: staffSummaryString,
        staffWorkHours,
      };
      
      setStatusAuditTrail((prev) => [...prev, entry]);
    }
    
    setPrevStatus(newStatus);
    setFormData((prev) => ({ ...prev, status: newStatus }));
  };

  const flattenedLocations = useMemo(() => {
    return (Array.isArray(companyLocations) ? companyLocations : []).flatMap((company) =>
      (Array.isArray(company.locations) ? company.locations : []).map((location) => ({
        ...location,
        companyId: company.id,
        companyName: company.companyName,
      }))
    );
  }, [companyLocations]);

  const selectedLocation = useMemo(() => {
    return flattenedLocations.find(l => String(l.id) === String(formData.locationId));
  }, [flattenedLocations, formData.locationId]);

  const selectedLocationId = formData.locationId;
  const selectedCustomerId = formData.customerId;

  useEffect(() => {
    const fetchUserAllocationTimes = async () => {
      try {
        const response = await fetch(`${baseUrl_SPB}/api/v1/usertimeallocation/getusertimeallocations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user time allocations');
        }

        const data = await response.json();
        setBookedItems(data);
      } catch (error) {
        console.error('Error fetching user time allocations:', error);
      }
    };

    if (open && token) {
      fetchUserAllocationTimes();
    }
  }, [open, token]);

  useEffect(() => {
    if (!open || !token) return;
    FetchLocationsService(token)
      .then((response) => setCompanyLocations(Array.isArray(response) ? response : []))
      .catch((error) => {
        console.error('Failed to fetch locations:', error);
        setCompanyLocations([]);
        setLoadError('Could not load locations for the job form.');
      });
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    if (initialJob || formData.locationId) return;
    const defaultLoc = (Array.isArray(companyLocations) ? companyLocations : [])
      .flatMap(company => Array.isArray(company.locations) ? company.locations.map(loc => ({ ...loc, companyId: company.id })) : [])
      .find(loc => loc.defaultLocation === 'Y');
    if (defaultLoc && defaultLoc.id) {
      setFormData(prev => ({ ...prev, locationId: defaultLoc.id, companyId: defaultLoc.companyId }));
    }
  }, [companyLocations, open, initialJob, formData.locationId]);

  useEffect(() => {
    if (!open || !token) return;
    setCustomersLoading(true);
    FetchBusinessCustomers(token)
      .then((response) => setCustomerOptions(Array.isArray(response) ? response : []))
      .catch((error) => {
        console.error('Failed to fetch customers:', error);
        setCustomerOptions([]);
        setLoadError((current) => current || 'Could not load customers for the job form.');
      })
      .finally(() => setCustomersLoading(false));
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    const draftJob = initialJob || {};
    const locationValue = draftJob.locationId || presetLocationId || '';
    const selectedDate = draftJob.selectedDateString || draftJob.date || new Date();

    setFormData((prev) => ({
      ...prev,
      id: draftJob.id || prev.id || uuidv4(),
      jobName: draftJob.jobName || draftJob.title || '',
      jobDetails: draftJob.jobDetails || '',
      customerId: draftJob.customerId || '',
      customerName: draftJob.customerName || '',
      customerEmail: draftJob.customerEmail || '',
      customerPhone: draftJob.customerPhone || '',
      companyId: draftJob.companyId || additionalUserInfo?.companyId || '',
      jobAddress: draftJob.jobAddress || '',
      locationId: locationValue,
      selectedDate: toDayjs(selectedDate),
      status: draftJob.status || 'Quote',
      category: draftJob.category || 'Standard',
      poNumber: draftJob.poNumber || '',
      invoiceId: draftJob.invoiceId || '',
      quoteId: draftJob.quoteId || '',
      bookedItemIds: Array.isArray(draftJob.bookedItemIds) ? draftJob.bookedItemIds : [],
    }));
    setCheckList(normalizeChecklist(draftJob.checkList));
    setStep(0);
    setLoadError('');

    const existingAssignees = Array.isArray(draftJob.assignees) && draftJob.assignees.length > 0
      ? draftJob.assignees
      : (draftJob.assigneeId ? [{ assigneeId: draftJob.assigneeId, assignee: draftJob.assignee || '' }] : []);
    setAssignees(existingAssignees);
    setPrimaryAssigneeId(existingAssignees[0]?.assigneeId || '');

    const slots = Array.isArray(draftJob.slots) ? draftJob.slots : [];
    if (existingAssignees.length > 0 && slots.length > 0) {
      setSelectedSlotsPerStaff({ [existingAssignees[0].assigneeId]: slots });
    } else {
      setSelectedSlotsPerStaff({});
    }
    setAssigneePickerValue(undefined);
    //setBookedItems(Array.isArray(draftJob.bookedItems) ? draftJob.bookedItems : []);

    setStatusAuditTrail(Array.isArray(draftJob.statusAuditTrail) ? draftJob.statusAuditTrail : []);
    setPrevStatus(draftJob.status || 'Quote');
  }, [additionalUserInfo?.companyId, initialJob, open, presetLocationId]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    const selectedCustomer = customerOptions.find((customer) => String(customer.id) === String(selectedCustomerId));
    if (!selectedCustomer) return;
    setFormData((prev) => ({
      ...prev,
      customerName: getCustomerName(selectedCustomer),
      customerEmail: selectedCustomer.email || '',
      customerPhone: selectedCustomer.phoneNumber || selectedCustomer.phone || '',
    }));
  }, [customerOptions, selectedCustomerId]);

  useEffect(() => {
    if (!selectedLocationId || !token) {
      setEmployeeOptions([]);
      return;
    }
    const selectedLocation = flattenedLocations.find((location) => String(location.id) === String(selectedLocationId));
    if (!selectedLocation) {
      setEmployeeOptions([]);
      return;
    }
    FetchEmployeesService(token, selectedLocation)
      .then((response) => setEmployeeOptions(Array.isArray(response) ? response : []))
      .catch((error) => {
        console.error('Failed to fetch employees for trade job form:', error);
        setEmployeeOptions([]);
      });
  }, [flattenedLocations, selectedLocationId, token]);

  useEffect(() => {
    if (!open || !token) return;
    FetchTradeJobs(token)
      .then((jobs) => setAllJobs(Array.isArray(jobs) ? jobs : []))
      .catch(() => setAllJobs([]));
  }, [open, token]);

  const selectedDateValue = formData.selectedDate;

  useEffect(() => {
    if (!selectedDateValue || !employeeOptions.length) {
      setAvailabilityData([]);
      return;
    }
    if (dayjs.isDayjs(selectedDateValue)) {
      const dateStr = selectedDateValue.format('DD-MM-YYYY'); // Corrected format to match slot checks
      const results = generateAvailability(employeeOptions, allJobs, jobId, dateStr);
      setAvailabilityData(results);
    }
  }, [selectedDateValue, employeeOptions, allJobs, jobId, bookedItems]);

  const addAssignee = (staffId) => {
    if (!staffId || assignees.some((a) => String(a.assigneeId) === String(staffId))) return;
    const staff = employeeOptions.find((e) => String(e.id) === String(staffId));
    if (!staff) return;
    const name = `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
    const next = [...assignees, { assigneeId: String(staffId), assignee: name }];
    setAssignees(next);
    if (!primaryAssigneeId) setPrimaryAssigneeId(String(staffId));
  };

  const removeAssignee = (staffId) => {
    const next = assignees.filter((a) => String(a.assigneeId) !== String(staffId));
    setAssignees(next);
    setSelectedSlotsPerStaff((prev) => {
      const copy = { ...prev };
      delete copy[String(staffId)];
      return copy;
    });
    if (String(primaryAssigneeId) === String(staffId)) {
      setPrimaryAssigneeId(next[0]?.assigneeId || '');
    }
  };

  function generateAvailability(staffList, allJobs, currentJobId, targetDateStr) {
    if (!staffList || !staffList.length) return [];

    // Directly returns the flat list array mapping the expected selected single date configuration
    return staffList.map((staff) => {
      const slots = SLOT_OPTIONS.map(({ value: slotValue }) => {
        
        const hasAnyBooking = allJobs.some(
          (job) =>
            Array.isArray(job.assignees) &&
            job.assignees.some((a) => String(a.assigneeId) === String(staff.id)) &&
            (job.date === targetDateStr || job.selectedDateString === targetDateStr) &&
            Array.isArray(job.slots) &&
            job.slots.includes(slotValue)
        ) || (bookedItems && bookedItems.some(item => 
            String(item.staffId) === String(staff.id) &&
            item.date === targetDateStr &&
            item.starttime === slotValue
        ));

        const slotForOtherJob = allJobs.some(
          (job) =>
            job.id !== currentJobId &&
            Array.isArray(job.assignees) &&
            job.assignees.some((a) => String(a.assigneeId) === String(staff.id)) &&
            (job.date === targetDateStr || job.selectedDateString === targetDateStr) &&
            Array.isArray(job.slots) &&
            job.slots.includes(slotValue)
        ) || (bookedItems && bookedItems.some(item => 
            String(item.staffId) === String(staff.id) &&
            item.date === targetDateStr &&
            item.starttime === slotValue &&
            item.jobId !== currentJobId
        ));

        return {
          available: !hasAnyBooking, 
          slotForOtherJob: slotForOtherJob
        };
      });

      return {
        staffId: staff.id,
        staffName: `${staff.firstName || ''} ${staff.lastName || ''}`.trim(),
        slots,
      };
    });
  }

  const handleAvailableSpotSelection = async (field, entry, idx, available, i, starttime, dateStr) => {
    if (String(entry.staffId) !== String(primaryAssigneeId)) {
      return;
    }

    const currentStaffSlots = selectedSlotsPerStaff[String(entry.staffId)] || [];
    const isCurrentlySelected = currentStaffSlots.includes(starttime);

    const computeEndTime = (timeStr) => {
      if (!timeStr || typeof timeStr !== 'string') return undefined;
      const [hourStr, minStr] = timeStr.split(':');
      let hour = parseInt(hourStr, 10);
      if (isNaN(hour)) return undefined;
      hour = (hour + 1) % 24;
      return `${hour < 10 ? '0' : ''}${hour}:${minStr}`;
    };

    try {
      if (!isCurrentlySelected) {
        const bookItemId = uuidv4();
        const bookedItem = {
          jobId: jobId,
          id: bookItemId,
          locationId: selectedLocation?.id || '',
          companyId: additionalUserInfo?.companyId || '',
          staffId: String(entry.staffId),
          date: formData.selectedDate ? formData.selectedDate.format('DD-MM-YYYY') : '',
          starttime: starttime,
          endtime: computeEndTime(starttime)
        };

        await AddBookedItem([bookedItem], token);

        setBookedItems(prev => [...prev, bookedItem]);
        setSelectedSlotsPerStaff(prev => ({
          ...prev,
          [String(entry.staffId)]: [...currentStaffSlots, starttime]
        }));
      } else {
        const targetDateStr = formData.selectedDate ? formData.selectedDate.format('DD-MM-YYYY') : '';
        const deletedBookedItems = bookedItems?.filter(
          item => item.date === targetDateStr && item.starttime === starttime && String(item.staffId) === String(entry.staffId)
        ) || [];

        const response = await DeleteBookedItem(deletedBookedItems, token);

        const updatedBookedItems = bookedItems?.filter(
          item => !(item.date === targetDateStr && item.starttime === starttime && String(item.staffId) === String(entry.staffId))
        ) || [];
        setBookedItems(updatedBookedItems);

        setSelectedSlotsPerStaff(prev => ({
          ...prev,
          [String(entry.staffId)]: currentStaffSlots.filter(t => t !== starttime)
        }));

        setFormData(prev => ({
          ...prev,
          bookedItemIds: prev.bookedItemIds
            ? prev.bookedItemIds.filter(id => {
                if (Array.isArray(response)) {
                  return !response.some(r => r && r.id === id);
                } else if (response && response.id) {
                  return response.id !== id;
                }
                return true;
              })
            : [],
        }));
      }
    } catch (error) {
      console.error('Failed to sync booking slot selection changes:', error);
      setLoadError('Roster synchronization failed. Please try again.');
    }
  };

   const deleteSlotData = (deletedBookedItemList) => {
    let slotData = formData.slots ? formData.slots : [];

    deletedBookedItemList.forEach(item => {
      slotData.forEach((slottime, index) => {
        if (item.starttime === slottime) {
          slotData.splice(index, 1);
        }
      })
    });
    setFormData(prev => ({ ...prev, 'slots': slotData }));
  }
  const handleDateChange = async (newDate) => {
    const oldDateStr = formData.selectedDate ? formData.selectedDate.format('DD-MM-YYYY') : '';
    setFormData((prev) => ({ ...prev, selectedDate: newDate }));
    setLoadError('');
    if (bookedItems && bookedItems.length > 0) {

      const deleteBookedItemsList = bookedItems?.filter(item => item.jobId === formData.id);
      const response=DeleteBookedItem(deleteBookedItemsList, token);
      setBookedItems(prev => prev.filter(item => item.jobId !== formData.id));

      setFormData(prev => ({
    ...prev,
    bookedItemIds: [],
  }));
    }
    

    setSelectedSlotsPerStaff({});
  };

  const allSelectedSlots = useMemo(() => {
    const set = new Set();
    Object.values(selectedSlotsPerStaff).forEach((slots) => slots.forEach((s) => set.add(s)));
    return Array.from(set).sort();
  }, [selectedSlotsPerStaff]);

  const openAddCustomer = () => {
    setNewCustomer({ firstName: '', lastName: '', email: '', phoneNumber: '', businessName: '', abn: '', note: '' });
    setAddCustomerError('');
    setAddCustomerOpen(true);
  };

  const handleSaveNewCustomer = async () => {
    if (!newCustomer.firstName.trim()) {
      setAddCustomerError('First name is required.');
      return;
    }
    if (!newCustomer.lastName.trim()) {
      setAddCustomerError('Last name is required.');
      return;
    }
    if (!newCustomer.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email)) {
      setAddCustomerError('A valid email address is required.');
      return;
    }
    if (newCustomer.abn && !/^\d{11}$/.test(newCustomer.abn.replace(/\s/g, ''))) {
      setAddCustomerError('ABN must be 11 digits.');
      return;
    }

    const payload = {
      ...newCustomer,
      companyName: additionalUserInfo?.companyName || '',
      companyId: additionalUserInfo?.companyId || '',
    };

    setAddCustomerSaving(true);
    setAddCustomerError('');
    try {
      const saved = await SaveBusinessCustomer(payload, token);
      const refreshed = await FetchBusinessCustomers(token);
      const nextList = Array.isArray(refreshed) ? refreshed : [];
      setCustomerOptions(nextList);
      const savedId = saved?.id || nextList.find((c) => c.email === newCustomer.email)?.id;
      if (savedId) {
        setFormData((prev) => ({
          ...prev,
          customerId: savedId,
          customerName: getCustomerName(saved || {}),
          customerEmail: saved?.email || newCustomer.email,
          customerPhone: saved?.phoneNumber || newCustomer.phoneNumber || '',
        }));
      }
      setAddCustomerOpen(false);
    } catch (error) {
      console.error('Failed to save customer:', error);
      setAddCustomerError('Failed to save customer. Please try again.');
    } finally {
      setAddCustomerSaving(false);
    }
  };

  const updateChecklistItem = (index, patch) => {
    setCheckList((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  };

  const generateNumber = (type) => {
    const prefix = type === 'quote' ? 'Q' : 'I';
    const uniqueId = uuidv4();
    return `${prefix}-${uniqueId}`;
  };

  const nextStep = async () => {
    if (step === 0) {
      if (!formData.jobName || !formData.jobDetails || !formData.customerId) {
        setLoadError('Please fill all required fields in this step.');
        return;
      }
    }
    if (step === 1) {
      if (!formData.status || !formData.locationId || !formData.selectedDate) {
        setLoadError('Please fill all required fields in this step.');
        return;
      }
      
      const isMandatoryStatus = formData.status === 'In-progress' || formData.status === 'Inspection';
      
      if (isMandatoryStatus) {
        if (!assignees.length) {
          setLoadError('Please assign at least one staff member for In-progress or Inspection phases.');
          return;
        }
        if (!allSelectedSlots.length) {
          setLoadError('Please select at least one time slot for In-progress or Inspection phases.');
          return;
        }
      }
    }
    setLoadError('');
    setStep((current) => Math.min(current + 1, STEP_TITLES.length - 1));
  };
  
  const previousStep = () => {
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async () => {
    try {
      let patchedData = { ...formData };
      if (formData.customerId && customerOptions.length) {
        const selectedCustomer = customerOptions.find((c) => String(c.id) === String(formData.customerId));
        if (selectedCustomer) {
          patchedData = {
            ...patchedData,
            customerName: getCustomerName(selectedCustomer),
            customerEmail: selectedCustomer.email || '',
            customerPhone: selectedCustomer.phoneNumber || selectedCustomer.phone || '',
          };
        }
      }
      if (formData.locationId && flattenedLocations.length) {
        const selectedLocation = flattenedLocations.find((l) => String(l.id) === String(formData.locationId));
        if (selectedLocation) {
          patchedData = {
            ...patchedData,
            jobAddress: selectedLocation.streetName || selectedLocation.locationName || '',
          };
        }
      }
      const selectedLocation = flattenedLocations.find((location) => String(location.id) === String(patchedData.locationId));
      const selectedDateString = patchedData.selectedDate && patchedData.selectedDate.format ? patchedData.selectedDate.format('DD-MM-YYYY') : '';
      const primaryAssignee = assignees.find((a) => String(a.assigneeId) === String(primaryAssigneeId)) || assignees[0];
      const payload = {
        ...(initialJob || {}),
        id: jobId,
        companyId: selectedLocation?.companyId || patchedData.companyId || additionalUserInfo?.companyId || '',
        jobName: patchedData.jobName,
        jobDetails: patchedData.jobDetails,
        customerId: patchedData.customerId || '',
        customerName: patchedData.customerName || '',
        customerEmail: patchedData.customerEmail || '',
        customerPhone: patchedData.customerPhone || '',
        jobAddress: patchedData.jobAddress || '',
        assigneeId: primaryAssignee?.assigneeId || '',
        assignee: primaryAssignee?.assignee || '',
        assignees,
        selectedDate: patchedData.selectedDate ? (patchedData.selectedDate.toDate ? patchedData.selectedDate.toDate() : patchedData.selectedDate) : new Date(),
        selectedDateString,
        date: selectedDateString,
        slots: allSelectedSlots,
        slotsPerStaff: selectedSlotsPerStaff,
        bookedItems,
        checkList,
        status: patchedData.status,
        category: patchedData.category,
        poNumber: patchedData.poNumber || '',
        invoiceId: patchedData.invoiceId || '',
        quoteId: patchedData.quoteId || '',
        locationId: selectedLocation?.id || patchedData.locationId || '',
        createdAt: initialJob?.createdAt || new Date().toISOString(),
        statusAuditTrail,
      };
      setSaving(true);
      const response = await AddTradeJob(payload, token);
      if (!response) {
        setLoadError('Unable to save trade job right now.');
        return;
      }
      onSaved({ ...payload, ...response });
      onClose();
    } catch (error) {
      console.error('Failed to save trade job:', error);
      setLoadError('Unable to save trade job right now.');
    } finally {
      setSaving(false);
    }
  };

  const openQIModal = async (type, item = null) => {
    setModalType(type);
    if (!item?.id) {
      setSelectedQIItem({ invoiceOrQuoteNumber: generateNumber(type) });
      if (type === 'quote') setShowQuoteModal(true);
      else setShowInvoiceModal(true);
    } else {
      try {
        const response = await axios.get(
          `${baseUrl_SPB}/api/v1/invorquote/getsingleinvquote?id=${item.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        let patchedItem = { ...item };
        if (item.customerName && item.customerId) {
          patchedItem.customerObj = { label: item.customerName, value: item.customerId };
        }
        if (item.id) {
          patchedItem.id = item.id;
        }
        setSelectedQIItem(patchedItem);
      } catch (error) {
        setSelectedQIItem({ id: item.id });
        console.error('Failed to fetch quote/invoice details:', error);
      }
      if (type === 'quote') setShowQuoteModal(true);
      else setShowInvoiceModal(true);
    }
  };

  const closeQIModal = () => {
    setShowQuoteModal(false);
    setShowInvoiceModal(false);
    setSelectedQIItem(null);
    setModalType(null);
  };

  const handleQISave = async (item, type) => {
    const payload = {
      ...item,
      id: item.id || null,
      invoiceOrQuoteNumber: item.invoiceOrQuoteNumber,
      customerId: item.customerId,
      customerEmail: item.customerEmail || '',
      customerName: item.customerName,
      customerPhone: item.customerPhone || '',
      dueDate: item.dueDate,
      dated: item.dated,
      taxOption: item.taxOption,
      invOrQuoteItems: item.items || item.invOrQuoteItems || [],
      invPaymentItems: item.payments || item.invPaymentItems || [],
      grandTotal: item.total,
      type: type,
      paymentRecords: item.payments || item.invPaymentItems || [],
    };
    try {
      const response = await axios.post(
        `${baseUrl_SPB}/api/v1/invorquote/save`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (type === 'invoice') {
        setFormData((prev) => ({ ...prev, invoiceId: response.data.id }));
        setInvoice(response.data);
      } else {
        setFormData((prev) => ({ ...prev, quoteId: response.data.id }));
        setQuote(response.data);
      }
      closeQIModal();
    } catch (error) {
      console.error(error);
    }
  };

  function renderFormContent() {
    return (
      <>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {asPage && step===3?
          <Card
                size="small"
                style={{ marginBottom: 8, borderColor: '#3f8600' }}
                title={
                  <span>
                   
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        
                      </Text>
                   
                  </span>
                }
                extra={
                  
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                    Save changes
                  </Button>
                  
                }
              ></Card>:null}
          <ProfitForecastView token={token} jobId={initialJob?.id} />
          {loadError ? <Alert type="error" showIcon message={loadError} /> : null}
          <Steps current={step} items={STEP_TITLES.map((title) => ({ title }))} size="small" />
          
          {step === 0 ? (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Title level={5} style={{ margin: 0 }}>Customer & Job Details</Title>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Job title</label>
                <Input
                  placeholder="Job title"
                  value={formData.jobName}
                  onChange={e => setFormData({ ...formData, jobName: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Job details</label>
                <Input.TextArea
                  rows={4}
                  placeholder="Describe the job"
                  value={formData.jobDetails}
                  onChange={e => setFormData({ ...formData, jobDetails: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Customer</label>
                <Select
                  showSearch
                  allowClear
                  loading={customersLoading}
                  placeholder="Search customer by name, email, phone, or business"
                  value={formData.customerId || undefined}
                  onChange={val => setFormData({ ...formData, customerId: val })}
                  options={customerOptions.map((customer) => ({
                    value: customer.id,
                    label: getCustomerName(customer) || customer.email || customer.id,
                    searchText: getCustomerSearchText(customer),
                  }))}
                />
                <Tooltip title="Add new customer">
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, height: 'auto', marginTop: -2 }}
                    onClick={openAddCustomer}
                  />
                </Tooltip>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Customer email</label>
                <Input placeholder="Customer email" value={formData.customerEmail} readOnly />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Customer phone</label>
                <Input placeholder="Customer phone" value={formData.customerPhone} readOnly />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Job address</label>
                <Input.TextArea
                  rows={3}
                  placeholder="Street, suburb, city"
                  value={formData.jobAddress}
                  onChange={e => setFormData({ ...formData, jobAddress: e.target.value })}
                />
              </div>
            </Space>
          ) : null}
          
          {step === 1 ? (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Title level={5} style={{ margin: 0 }}>Scheduling</Title>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Status</label>
                <Select
                  value={formData.status}
                  options={JOB_STATUSES.map((status) => ({ value: status, label: status }))}
                  onChange={val => {
                    setFormData({ ...formData, status: val });
                    handleStatusChange(val);
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Location</label>
                <Select
                  value={formData.locationId}
                  placeholder="Select location"
                  onChange={val => setFormData({ ...formData, locationId: val })}
                  options={flattenedLocations.map((location) => ({
                    value: location.id,
                    label: `${location.streetName || location.locationName || ''}${location.suburb ? `${location.suburb}` : ''}`,
                  }))}
                />
              </div>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 6 }}>Assign Staff</div>
                <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                  <Select
                    style={{ flex: 1 }}
                    placeholder="Select staff to add"
                    value={assigneePickerValue}
                    showSearch
                    optionFilterProp="label"
                    onChange={(val) => {
                      setAssigneePickerValue(val);
                      if (val) {
                        addAssignee(val);
                        setAssigneePickerValue(undefined);
                      }
                    }}
                    options={employeeOptions
                      .filter((e) => !assignees.some((a) => String(a.assigneeId) === String(e.id)))
                      .map((e) => ({
                        value: e.id,
                        label: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email || e.id,
                      }))}
                    suffixIcon={<UserAddOutlined />}
                  />
                </Space.Compact>
                {assignees.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                    {assignees.map((a) => {
                      const isPrimary = String(a.assigneeId) === String(primaryAssigneeId);
                      return (
                        <Tag
                          key={a.assigneeId}
                          closable
                          color={isPrimary ? 'blue' : 'default'}
                          onClose={() => removeAssignee(a.assigneeId)}
                          onClick={() => setPrimaryAssigneeId(String(a.assigneeId))}
                          style={{ cursor: 'pointer', fontSize: 13, padding: '2px 8px' }}
                        >
                          {a.assignee}{isPrimary ? ' (Primary)' : ''}
                        </Tag>
                      );
                    })}
                  </div>
                )}
                {assignees.length > 1 && (
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Tap a name to set as primary assignee.</div>
                )}
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Job date</label>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  value={formData.selectedDate}
                  onChange={handleDateChange}
                />
              </div>
              {availabilityData && availabilityData.length > 0 ? (
                <div>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                    Time Slots
                    {allSelectedSlots.length > 0 && (
                      <span style={{ fontWeight: 400, color: '#1677ff', marginLeft: 8, fontSize: 12 }}>
                        Selected: {allSelectedSlots.join(', ')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                    <span style={{ background: '#e6f4ea', border: '1px solid #4caf50', padding: '1px 6px', borderRadius: 3, marginRight: 6 }}>Available</span>
                    <span style={{ background: '#1677ff', color: '#fff', padding: '1px 6px', borderRadius: 3, marginRight: 6 }}>Selected</span>
                    <span style={{ background: '#fafafa', border: '1px solid #e0e0e0', padding: '1px 6px', borderRadius: 3 }}>Booked/unavailable</span>
                  </div>
                  {availabilityData.map((staffEntry) => (
                    <SlotGridWeb
                      key={staffEntry.staffId}
                      staffEntry={staffEntry}
                      primaryAssigneeId={primaryAssigneeId}
                      selectedSlots={selectedSlotsPerStaff[String(staffEntry.staffId)] || []}
                      onToggleSlot={handleAvailableSpotSelection}
                      assigneeIds={assignees.map((a) => String(a.assigneeId))}
                    />
                  ))}
                </div>
              ) : (
                selectedDateValue && employeeOptions.length > 0
                  ? <div style={{ color: '#888', fontSize: 13 }}>Loading availability…</div>
                  : selectedDateValue
                    ? <div style={{ color: '#888', fontSize: 13 }}>Select a location to see staff availability.</div>
                    : <div style={{ color: '#888', fontSize: 13 }}>Select a date to see time slots.</div>
              )}
            </Space>
          ) : null}
          
          {step === 2 ? (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Title level={5} style={{ margin: 0 }}>Checklist & Execution</Title>
              <TradeJobChecklistExecutionView value={checkList} onChange={setCheckList} />
              <Divider style={{ margin: '8px 0' }} />
              <Space>
                <Button type="primary" onClick={() => openQIModal('quote')}>Create Quote</Button>
                <Button type="primary" onClick={() => openQIModal('invoice')}>Create Invoice</Button>
                <LinkInvoiceQuoteFeature
                  onLinkInvoice={(item) => {
                    setFormData((prev) => ({ ...prev, invoiceId: item.id }));
                    setInvoice(item);
                  }}
                  onLinkQuote={(item) => {
                    setFormData((prev) => ({ ...prev, quoteId: item.id }));
                    setQuote(item);
                  }}
                />
              </Space>
              <div style={{ marginTop: 12 }}>
                <label style={{ fontWeight: 500 }}>Linked quote</label>
                {formData.quoteId ? (
                  <Space>
                    <span style={{ fontWeight: 500, color: '#1677ff' }}>{formData.quoteId}</span>
                    <Button size="small" onClick={() => openQIModal('quote', quote)}>View</Button>
                    <Button size="small" type="primary" onClick={() => openQIModal('quote', quote)}>Edit</Button>
                  </Space>
                ) : (
                  <span style={{ color: '#888' }}>No quote linked</span>
                )}
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ fontWeight: 500 }}>Linked invoice</label>
                {formData.invoiceId ? (
                  <Space>
                    <span style={{ fontWeight: 500, color: '#1677ff' }}>{formData.invoiceId}</span>
                    <Button size="small" onClick={() => openQIModal('invoice', invoice)}>View</Button>
                    <Button size="small" type="primary" onClick={() => openQIModal('invoice', invoice)}>Edit</Button>
                  </Space>
                ) : (
                  <span style={{ color: '#888' }}>No invoice linked</span>
                )}
              </div>
              <Modal
                open={showQuoteModal}
                title={selectedQIItem && selectedQIItem.id ? 'Edit/View Quote' : 'Create Quote'}
                onCancel={closeQIModal}
                footer={null}
                width={900}
                destroyOnClose
              >
                <TradeJobCreateQuoteView
                  onSave={(item) => handleQISave(item, 'quote')}
                  item={selectedQIItem}
                  onCancel={closeQIModal}
                  type="quote"
                />
              </Modal>
              <Modal
                open={showInvoiceModal}
                title={selectedQIItem && selectedQIItem.id ? 'Edit/View Invoice' : 'Create Invoice'}
                onCancel={closeQIModal}
                footer={null}
                width={900}
                destroyOnClose
              >
                <TradeJobCreateInvoiceView
                  onSave={(item) => handleQISave(item, 'invoice')}
                  item={selectedQIItem}
                  onCancel={closeQIModal}
                  type="invoice"
                />
              </Modal>
            </Space>
          ) : null}
          
          {step === 3 ? (
            <>
            
          
            <Space direction="vertical" style={{ width: '100%' }} size={12}>

              <Title level={5} style={{ margin: 0 }}>Admin / Metadata</Title>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Category</label>
                <Select
                  value={formData.category}
                  options={JOB_CATEGORIES.map((category) => ({ value: category, label: category }))}
                  onChange={val => setFormData({ ...formData, category: val })}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>PO number</label>
                <Input
                  placeholder="Optional PO number"
                  value={formData.poNumber}
                  onChange={e => setFormData({ ...formData, poNumber: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Company ID</label>
                <Input
                  placeholder="Optional company ID override"
                  value={formData.companyId}
                  onChange={e => setFormData({ ...formData, companyId: e.target.value })}
                />
              </div>
              <div style={{ marginTop: 24 }}>
                <Title level={5} style={{ margin: 0 }}>Status Audit Trail</Title>
                {statusAuditTrail.length === 0 && (
                  <div style={{ color: '#888', fontSize: 13 }}>No status changes recorded yet.</div>
                )}
                {statusAuditTrail.length > 0 && (
                  <ul style={{ paddingLeft: 18 }}>
                    {statusAuditTrail.map((entry, idx) => (
                      <li key={idx} style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 600 }}>{entry.fromStatus}</span> → <span style={{ fontWeight: 600 }}>{entry.toStatus}</span>
                        <span style={{ color: '#1677ff' }}> {entry.staffNames ? `(Staff: ${entry.staffNames})` : ''}</span><br />
                        <span style={{ fontSize: 12, color: '#888' }}>By: {entry.changedBy} at {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Space>
            </>
          ) : null}
          
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={previousStep} disabled={step === 0}>Back</Button>
            <Space>
              <Text type="secondary">Step {step + 1} of {STEP_TITLES.length}</Text>
              {step < STEP_TITLES.length - 1 ? (
                <Button type="primary" onClick={nextStep}>Next</Button>
              ) : null}
            </Space>
          </Space>
        </Space>
        
        <Modal
          open={addCustomerOpen}
          title="Add new customer"
          onCancel={() => setAddCustomerOpen(false)}
          onOk={handleSaveNewCustomer}
          okText="Save customer"
          confirmLoading={addCustomerSaving}
          destroyOnClose
          width={520}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {addCustomerError ? <Alert type="error" showIcon message={addCustomerError} /> : null}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>First name<span style={{ color: 'red' }}>*</span></label>
              <Input
                value={newCustomer.firstName}
                onChange={(e) => setNewCustomer((c) => ({ ...c, firstName: e.target.value }))}
                placeholder="First name"
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>Last name<span style={{ color: 'red' }}>*</span></label>
              <Input
                value={newCustomer.lastName}
                onChange={(e) => setNewCustomer((c) => ({ ...c, lastName: e.target.value }))}
                placeholder="Last name"
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>Email<span style={{ color: 'red' }}>*</span></label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer((c) => ({ ...c, email: e.target.value }))}
                placeholder="Email address"
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>Phone number</label>
              <Input
                value={newCustomer.phoneNumber}
                onChange={(e) => setNewCustomer((c) => ({ ...c, phoneNumber: e.target.value }))}
                placeholder="Phone number"
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>Business name</label>
              <Input
                value={newCustomer.businessName}
                onChange={(e) => setNewCustomer((c) => ({ ...c, businessName: e.target.value }))}
                placeholder="Business name (if relevant)"
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>ABN</label>
              <Input
                value={newCustomer.abn}
                onChange={(e) => setNewCustomer((c) => ({ ...c, abn: e.target.value }))}
                placeholder="11-digit ABN (if relevant)"
                maxLength={14}
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 500 }}>Notes</label>
              <Input.TextArea
                rows={3}
                value={newCustomer.note}
                onChange={(e) => setNewCustomer((c) => ({ ...c, note: e.target.value }))}
                placeholder="Additional notes"
              />
            </div>
          </Space>
        </Modal>
      </>
    );
  }

  function renderDrawerContent() {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        width={760}
        title={initialJob?.id ? 'Edit Trade Job' : 'Create Trade Job'}
        destroyOnClose
        extra={(
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              {initialJob?.id ? 'Save changes' : 'Create job'}
            </Button>
          </Space>
        )}
      >
        {renderFormContent()}
      </Drawer>
    );
  }

  if (asPage) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 2px 8px #f0f1f2' }}>
        <h2>{initialJob?.id ? 'Edit Trade Job' : 'Create Trade Job'}</h2>
        {renderFormContent()}
         
      </div>
    );
  }
  return renderDrawerContent();
}